import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";

import {
  listTreasuryPeriodBalances,
  saveTreasuryPeriodCashCounts,
} from "../api/cierresTesoreriaApi";
import { fmtDMY, fmtYMD, ymdToDate } from "../../../../shared/utils/dateFormat";
import {
  amountInputValue,
  calculateCajaTotals,
  formatMoney,
  moneyInputsAreEqual,
  parseMoneyValue,
  resolveMoneyInput,
} from "../utils/lienzoUtils";

const gridColumns = "1.2fr 140px 140px 140px 170px 140px";
const FUTURE_CLOSURE_MESSAGE = "El cierre no puede completarse antes de su fecha final.";

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.detail ||
    error?.data?.message ||
    error?.message ||
    "No se pudo completar la operación."
  );
}

function moneyColor(value, fallback = "primary.900") {
  if (value === null || value === undefined || value === "") return fallback;
  const amount = Number(value || 0);
  if (amount > 0) return "#159251";
  if (amount < 0) return "#c91f1f";
  return fallback;
}

function normalizeBalanceRow(row) {
  return {
    ...row,
    cashCountInput: amountInputValue(row.cashCount),
    errors: {},
    dirty: false,
  };
}

function cloneRows(rows) {
  return rows.map((row) => ({
    ...row,
    errors: { ...(row.errors || {}) },
  }));
}

function getRowErrorItems(error) {
  return error?.data?.rows || error?.response?.data?.rows || [];
}

function getSavedErrorItems(error) {
  return error?.data?.saved || error?.response?.data?.saved || [];
}

function getRowKey(row) {
  return String(row.id ?? row.accountId);
}

export default function CajaCierreTable({
  period,
  readonly,
  completing = false,
  navigation,
  hideHeader = false,
  onComplete,
  onDirtyChange,
}) {
  const [persistedRows, setPersistedRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [focusedRowKey, setFocusedRowKey] = useState(null);

  const editable = !readonly;

  const totals = useMemo(() => calculateCajaTotals(rows), [rows]);

  const dirtyRowEntries = useMemo(
    () =>
      editable
        ? rows
            .map((row, rowIndex) => ({ row, rowIndex }))
            .filter(({ row }) => row.dirty)
        : [],
    [editable, rows]
  );
  const dirtyRows = useMemo(
    () => dirtyRowEntries.map(({ row }) => row),
    [dirtyRowEntries]
  );
  const hasDirtyRows = dirtyRows.length > 0;
  const hasDirtyErrors = dirtyRows.some((row) => Boolean(row.errors?.cashCount));

  useEffect(() => {
    onDirtyChange?.(hasDirtyRows);
  }, [hasDirtyRows, onDirtyChange]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const balances = await listTreasuryPeriodBalances(period.id);
        if (!alive) return;
        const normalized = balances.map(normalizeBalanceRow);
        setPersistedRows(normalized);
        setRows(cloneRows(normalized));
      } catch (err) {
        if (!alive) return;
        setError(errorToMessage(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      onDirtyChange?.(false);
    };
  }, [period.id, onDirtyChange]);

  const validateCashCount = (value) => {
    const parsed = parseMoneyValue(value);
    if (Number.isNaN(parsed)) return "Ingresá un importe válido con hasta 2 decimales.";
    if (parsed !== null && parsed < 0) return "El arqueo no puede ser negativo.";
    if (parsed !== null && parsed > 9999999999999.99) {
      return "El arqueo supera el importe máximo permitido.";
    }
    return "";
  };

  const updateCashCount = (rowKey, value) => {
    if (!editable || saving) return;
    setRows((prev) =>
      prev.map((row) => {
        if (getRowKey(row) !== String(rowKey)) return row;
        const resolution = resolveMoneyInput(row.cashCountInput, value);
        if (!resolution.accepted) {
          return {
            ...row,
            errors: {
              ...row.errors,
              cashCount: "Ingresá un importe válido con hasta 2 decimales.",
            },
          };
        }
        const persisted = persistedRows.find((item) => getRowKey(item) === getRowKey(row));
        const nextError = validateCashCount(value);
        return {
          ...row,
          cashCountInput: value,
          dirty: !moneyInputsAreEqual(value, persisted?.cashCountInput ?? ""),
          errors: { ...row.errors, cashCount: nextError },
        };
      })
    );
  };

  const buildPayload = () =>
    dirtyRows.map((row) => ({
      id: row.id,
      accountId: row.accountId,
      cashCount: parseMoneyValue(row.cashCountInput),
    }));

  const saveChanges = async ({ silent = false } = {}) => {
    if (!editable || saving) return false;
    setFocusedRowKey(null);
    if (!hasDirtyRows) return true;

    const validationErrors = new Map(
      dirtyRows.map((row) => [getRowKey(row), validateCashCount(row.cashCountInput)])
    );
    setRows((prev) =>
      prev.map((row) => {
        if (!row.dirty) return row;
        const nextError = validationErrors.get(getRowKey(row)) || "";
        return { ...row, errors: { ...row.errors, cashCount: nextError } };
      })
    );
    if ([...validationErrors.values()].some(Boolean)) return false;

    setSaving(true);
    setError("");

    try {
      const saved = await saveTreasuryPeriodCashCounts(period.id, buildPayload());
      const savedByAccount = new Map(saved.map((item) => [String(item.accountId), normalizeBalanceRow(item)]));
      const nextRows = rows.map((row) => savedByAccount.get(String(row.accountId)) || row);
      const cleanRows = nextRows.map((row) => ({ ...row, dirty: false, errors: {} }));
      setPersistedRows(cleanRows);
      setRows(cloneRows(cleanRows));
      if (!silent) setToast("Arqueos guardados correctamente.");
      return true;
    } catch (err) {
      const rowErrors = getRowErrorItems(err);
      const partiallySaved = getSavedErrorItems(err);
      const savedByAccount = new Map(
        partiallySaved.map((item) => [String(item.accountId), normalizeBalanceRow(item)])
      );

      if (rowErrors.length) {
        setRows((prev) =>
          prev.map((row, rowIndex) => {
            const savedRow = savedByAccount.get(String(row.accountId));
            if (savedRow) return savedRow;

            const rowError = rowErrors.find(
              (item) => {
                const batchIndex = Number(item.index);
                const indexedDirtyRow = Number.isInteger(batchIndex)
                  ? dirtyRowEntries[batchIndex]
                  : null;
                return (
                  String(item.accountId) === String(row.accountId) ||
                  String(item.id) === String(row.id) ||
                  indexedDirtyRow?.rowIndex === rowIndex
                );
              }
            );
            if (!rowError) return row;
            return {
              ...row,
              errors: { ...row.errors, [rowError.field || "cashCount"]: rowError.message },
            };
          })
        );
        if (partiallySaved.length) {
          setPersistedRows((prev) =>
            prev.map((row) => savedByAccount.get(String(row.accountId)) || row)
          );
        }
      }
      setError(errorToMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCompleteConfirmation = async () => {
    if (!editable || saving || completing || loading) return;
    setFocusedRowKey(null);
    const saved = await saveChanges({ silent: true });
    if (!saved) return;
    setError("");
    setConfirmCompleteOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (!editable || completing) return;
    if (period.periodEnd > fmtYMD(new Date())) {
      setError(FUTURE_CLOSURE_MESSAGE);
      setConfirmCompleteOpen(false);
      return;
    }

    setError("");
    try {
      await onComplete?.(period.id);
      setConfirmCompleteOpen(false);
    } catch (err) {
      setError(errorToMessage(err));
    }
  };

  const renderMoney = (value, color, { placeholder = false } = {}) => {
    if (placeholder || value === null || value === undefined) {
      return (
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: "neutral.500", textAlign: "right" }}>
          —
        </Typography>
      );
    }

    return (
      <Typography sx={{ fontSize: 14, fontWeight: 800, color, textAlign: "right" }}>
        {formatMoney(value)}
      </Typography>
    );
  };

  const renderRow = (row) => {
    const rowKey = getRowKey(row);
    const isFocused = focusedRowKey === rowKey;
    const parsedCashCount = parseMoneyValue(row.cashCountInput);
    const displayValue = isFocused
      ? row.cashCountInput
      : row.cashCountInput === "" || row.cashCountInput == null
        ? ""
        : Number.isNaN(parsedCashCount)
          ? row.cashCountInput
          : formatMoney(parsedCashCount);

    return (
      <Box
        key={rowKey}
        sx={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          minWidth: 920,
          borderBottom: "1px solid rgba(0,0,0,.08)",
          alignItems: "center",
          minHeight: 52,
          bgcolor: row.errors?.cashCount ? "rgba(255, 235, 238, 0.62)" : "transparent",
        }}
      >
        <Typography sx={{ px: 2, fontSize: 14, fontWeight: 800, color: "primary.900" }}>
          {row.accountName || "Cuenta sin nombre"}
        </Typography>
        <Box sx={{ px: 1.5 }}>{renderMoney(row.income, "#159251")}</Box>
        <Box sx={{ px: 1.5 }}>{renderMoney(row.expense, "#c91f1f")}</Box>
        <Box sx={{ px: 1.5 }}>{renderMoney(row.balance, moneyColor(row.balance))}</Box>
        <Box sx={{ px: 1.5 }}>
          {editable ? (
            <>
              <TextField
                value={displayValue}
                onFocus={() => setFocusedRowKey(rowKey)}
                onBlur={() => {
                  setFocusedRowKey(null);
                  const parsed = parseMoneyValue(row.cashCountInput);
                  if (!Number.isNaN(parsed) && parsed !== null) {
                    updateCashCount(rowKey, amountInputValue(parsed));
                  }
                }}
                onChange={(event) => updateCashCount(rowKey, event.target.value)}
                disabled={saving}
                size="small"
                fullWidth
                error={Boolean(row.errors?.cashCount)}
                inputProps={{ inputMode: "decimal" }}
                sx={{
                  maxWidth: 140,
                  "& .MuiOutlinedInput-root": {
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: "white",
                    fontWeight: 800,
                  },
                  "& input": {
                    py: 0.8,
                    textAlign: "right",
                    fontSize: 14,
                    fontWeight: 800,
                    color: "primary.900",
                  },
                }}
              />
              {row.errors?.cashCount ? (
                <Typography sx={{ mt: 0.4, fontSize: 10.5, fontWeight: 700, color: "error.700" }}>
                  {row.errors.cashCount}
                </Typography>
              ) : null}
            </>
          ) : (
            renderMoney(row.cashCount, "primary.900")
          )}
        </Box>
        <Box sx={{ px: 1.5 }}>
          {renderMoney(row.difference, moneyColor(row.difference, "neutral.600"))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: {
          xs: "auto",
          md: hideHeader ? "calc(100vh - 236px)" : "calc(100vh - 170px)",
        },
      }}
    >
      {!hideHeader ? (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
        <Box
          sx={{
            px: { xs: 1.4, sm: 1.8 },
            py: 1.2,
            borderRadius: "16px",
            bgcolor: "rgba(244, 245, 251, 0.88)",
            border: "1px solid rgba(255,255,255,0.8)",
            boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.18)",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "neutral.600", mb: 0.2 }}>
                Cierre seleccionado
              </Typography>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 900,
                  color: "primary.900",
                  lineHeight: 1.2,
                }}
              >
                Cierre {fmtDMY(ymdToDate(period.periodStart))} -{" "}
                {fmtDMY(ymdToDate(period.periodEnd))}
              </Typography>
            </Box>

            <Chip
              label={readonly ? "Completado" : "En trabajo"}
              size="small"
              sx={{
                flexShrink: 0,
                borderRadius: "999px",
                bgcolor: readonly ? "rgba(185, 246, 202, 0.72)" : "secondary.100",
                color: "primary.900",
                fontSize: 12,
                fontWeight: 900,
              }}
            />
          </Stack>
        </Box>

        {editable ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="flex-end"
          >
            <Button
              onClick={() => saveChanges()}
              disabled={!hasDirtyRows || hasDirtyErrors || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                bgcolor: "primary.500",
                color: "#fff",
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                px: 2.5,
                py: 1,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "primary.600" },
                "&.Mui-disabled": {
                  bgcolor: "primary.500",
                  color: "rgba(255, 255, 255, 0.72)",
                  opacity: 0.5,
                },
              }}
            >
              Guardar trabajo
            </Button>
            <Button
              onClick={handleOpenCompleteConfirmation}
              disabled={saving || completing || loading}
              startIcon={
                saving || completing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                bgcolor: "primary.500",
                color: "#fff",
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                px: 2.5,
                py: 1,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "primary.600" },
                "&.Mui-disabled": {
                  bgcolor: "primary.500",
                  color: "rgba(255, 255, 255, 0.72)",
                  opacity: 0.5,
                },
              }}
            >
              Completar cierre
            </Button>
          </Stack>
        ) : null}
        </Stack>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: "20px",
          bgcolor: "rgba(255,255,255,0.86)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {navigation}

        {error ? (
          <Alert severity="error" onClose={() => setError("")} sx={{ my: 1.5, borderRadius: "14px" }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            mt: 1,
            borderRadius: "20px",
            bgcolor: "neutral.50",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflow: "auto",
              position: "relative",
              maxHeight: { xs: 420, md: "calc(100vh - 320px)" },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                minWidth: 920,
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "neutral.600",
              }}
            >
              {["Cuenta", "Ingreso", "Egreso", "Saldo", "Arqueo de Caja", "Diferencia"].map((label) => (
                <Typography
                  key={label}
                  sx={{
                    px: 2,
                    py: 1.25,
                    fontSize: 14,
                    fontWeight: 800,
                    color: "logoblanco",
                    textAlign: label === "Cuenta" ? "left" : "right",
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            {loading ? (
              <Stack alignItems="center" spacing={1} sx={{ py: 5, minWidth: 920, bgcolor: "neutral.50" }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                  Cargando caja del cierre...
                </Typography>
              </Stack>
            ) : rows.length === 0 ? (
              <Stack alignItems="center" spacing={0.7} sx={{ py: 5, minWidth: 920, bgcolor: "neutral.50" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                  No hay balances para este cierre.
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "neutral.600" }}>
                  La caja se muestra cuando el período tiene balances por cuenta.
                </Typography>
              </Stack>
            ) : (
              rows.map(renderRow)
            )}

            {rows.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridColumns,
                  minWidth: 920,
                  position: "sticky",
                  bottom: 0,
                  zIndex: 1,
                  bgcolor: "neutral.100",
                  borderTop: "2px solid rgba(0,0,0,0.12)",
                  alignItems: "center",
                  minHeight: 44,
                  py: 1,
                }}
              >
                <Typography sx={{ px: 2, fontSize: 13, fontWeight: 900, color: "primary.900" }}>
                  TOTAL
                </Typography>
                <Box sx={{ px: 1.5 }}>{renderMoney(totals.income, "#159251")}</Box>
                <Box sx={{ px: 1.5 }}>{renderMoney(totals.expense, "#c91f1f")}</Box>
                <Box sx={{ px: 1.5 }}>{renderMoney(totals.balance, moneyColor(totals.balance))}</Box>
                {/* Arqueo de Caja y Diferencia: sin total */}
                <Box />
                <Box />
              </Box>
            )}
          </Box>
        </Box>

      </Paper>

      <Dialog
        open={confirmCompleteOpen}
        onClose={completing ? undefined : () => setConfirmCompleteOpen(false)}
        PaperProps={{ sx: { borderRadius: "18px", maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
          Completar cierre
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "neutral.700" }}>
            ¿Deseas completar este cierre? Una vez completado, los movimientos y la caja quedarán
            en modo de solo lectura.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmCompleteOpen(false)}
            disabled={completing}
            sx={{ color: "primary.900", borderRadius: "24px", textTransform: "none", fontWeight: 800 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmComplete}
            disabled={completing}
            startIcon={
              completing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />
            }
            sx={{
              bgcolor: "primary.900",
              color: "white",
              px: 2,
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 800,
              "&:hover": { bgcolor: "primary.800" },
            }}
          >
            Completar cierre
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2600}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setToast("")} sx={{ borderRadius: "14px" }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
